// services/taxonomySyncService.ts

import { logger } from '../utils/logger';
import { getBackendUrl, isTaxonomyFeaturesEnabled } from '../utils/environment.config';
import { refreshTaxonomy } from './enhancedTaxonomyService';
import api from '../http/axiosClient';

interface SyncState {
  isInitialized: boolean;
  currentVersion: string | null;
  lastSyncTime: number | null;
  lastError: string | null;
  connectionState: 'connected' | 'disconnected' | 'error' | 'degraded';
}

interface LayerCount {
  count: number;
  layers: string[];
}

interface CategoryCount {
  layer: string;
  count: number;
}

interface SubcategoryCount {
  layer: string;
  category: string;
  count: number;
}

interface LayerSubcategoryCounts {
  layer: string;
  counts: Array<{ category: string; count: number }>;
  totalCategories: number;
  totalSubcategories: number;
}

interface TaxonomyIndex {
  version: string;
  lastUpdated: string;
  totalLayers: number;
  layers: Record<
    string,
    {
      totalCategories: number;
      totalSubcategories: number;
      categories: Record<string, { subcategoryCount: number }>;
    }
  >;
  source: 'backend' | 'frontend';
}

class TaxonomySyncService {
  private static getApiBase(): string {
    const backendUrl = getBackendUrl();
    return `${backendUrl}/taxonomy`;
  }

  private static readonly SYNC_INTERVAL = 10 * 60 * 1000; // 10 minutes (REDUCED from 5 min)
  private static readonly CACHE_KEY = 'nna_taxonomy_sync_cache';
  private static readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  private syncState: SyncState = {
    isInitialized: false,
    currentVersion: null,
    lastSyncTime: null,
    lastError: null,
    connectionState: 'disconnected',
  };

  private syncInterval: NodeJS.Timeout | null = null;
  private eventListeners: Array<(state: SyncState) => void> = [];

  // Initialize the sync service
  async initializeSync(): Promise<void> {
    // Check if taxonomy features are enabled
    if (!isTaxonomyFeaturesEnabled()) {
      logger.info('🚫 Taxonomy features disabled by feature flag');
      this.syncState.connectionState = 'disconnected';
      this.syncState.isInitialized = true; // Mark as initialized to prevent repeated checks
      this.notifyStateChange();
      return;
    }

    // Prevent duplicate initialization
    if (this.syncState.isInitialized) {
      logger.info('🔄 Taxonomy sync service already initialized, skipping...');
      return;
    }

    try {
      logger.info('🔄 Initializing taxonomy sync service...');

      // Sync initial index
      const index = await this.syncIndex();

      // Update state
      this.syncState.isInitialized = true;
      this.syncState.currentVersion = index.version;
      this.syncState.lastSyncTime = Date.now();
      this.syncState.connectionState = 'connected';
      this.syncState.lastError = null;

      // Start background processes with protection against duplicate initialization
      if (!this.syncInterval) {
        this.startBackgroundSync();
      }

      logger.info(
        `✅ Taxonomy sync initialized (version ${index.version})`
      );
      this.notifyStateChange();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.syncState.lastError = errorMessage;
      this.syncState.connectionState = 'error';

      logger.error('❌ Failed to initialize taxonomy sync:', error);
      this.notifyStateChange();
      throw error;
    }
  }

  // Sync the complete taxonomy index
  async syncIndex(): Promise<TaxonomyIndex> {
    // Check if taxonomy features are enabled
    if (!isTaxonomyFeaturesEnabled()) {
      logger.info('🚫 Taxonomy features disabled, returning cached or fallback data');
      
      // Try to return cached data if available
      const cached = this.getCachedIndex();
      if (cached) {
        return cached;
      }
      
      // Return fallback empty index if no cache available
      throw new Error('Taxonomy features are disabled and no cached data available');
    }

    try {
      logger.info('📥 Syncing taxonomy index...');

      const response = await api.get<TaxonomyIndex>(
        `${TaxonomySyncService.getApiBase()}/index`
      );
      const index: TaxonomyIndex = response.data;

      // Cache the index
      this.cacheIndex(index);

      // Update sync state
      this.syncState.lastSyncTime = Date.now();
      this.syncState.currentVersion = index.version;
      this.syncState.lastError = null;

      logger.info(
        `✅ Taxonomy index synced successfully (version ${index.version})`
      );
      this.notifyStateChange();

      return index;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Sync failed';
      this.syncState.lastError = errorMessage;
      this.syncState.connectionState = 'error';

      logger.error('❌ Failed to sync taxonomy index:', error);
      this.notifyStateChange();
      throw error;
    }
  }


  // Get layer count
  async getLayerCount(): Promise<LayerCount> {
    try {
      const response = await api.get<LayerCount>(
        `${TaxonomySyncService.getApiBase()}/layer-count`
      );
      return response.data;
    } catch (error) {
      logger.error('❌ Failed to get layer count:', error);
      throw error;
    }
  }

  // Get category count for a layer
  async getCategoryCount(layer: string): Promise<CategoryCount> {
    try {
      const response = await api.get<CategoryCount>(
        `${TaxonomySyncService.getApiBase()}/layers/${layer}/category-count`
      );
      return response.data;
    } catch (error) {
      logger.error(
        `❌ Failed to get category count for layer ${layer}:`,
        error
      );
      throw error;
    }
  }

  // Get subcategory count for a category
  async getSubcategoryCount(
    layer: string,
    category: string
  ): Promise<SubcategoryCount> {
    try {
      const response = await api.get<SubcategoryCount>(
        `${TaxonomySyncService.getApiBase()}/layers/${layer}/categories/${category}/subcategory-count`
      );
      return response.data;
    } catch (error) {
      logger.error(
        `❌ Failed to get subcategory count for ${layer}.${category}:`,
        error
      );
      throw error;
    }
  }

  // Get all subcategory counts for a layer
  async getLayerSubcategoryCounts(
    layer: string
  ): Promise<LayerSubcategoryCounts> {
    try {
      const response = await api.get<LayerSubcategoryCounts>(
        `${TaxonomySyncService.getApiBase()}/layers/${layer}/subcategory-counts`
      );
      return response.data;
    } catch (error) {
      logger.error(
        `❌ Failed to get subcategory counts for layer ${layer}:`,
        error
      );
      throw error;
    }
  }

  // Get cached index
  getCachedIndex(): TaxonomyIndex | null {
    try {
      const cached = localStorage.getItem(TaxonomySyncService.CACHE_KEY);
      if (!cached) return null;

      const parsedCache = JSON.parse(cached);

      // Check cache validity
      if (
        Date.now() - parsedCache.timestamp >
        TaxonomySyncService.CACHE_DURATION
      ) {
        this.clearCache();
        return null;
      }

      return parsedCache.data;
    } catch (error) {
      logger.warn('Failed to read cached index:', error);
      return null;
    }
  }

  // Cache taxonomy index
  private cacheIndex(index: TaxonomyIndex): void {
    try {
      const cacheData = {
        data: index,
        timestamp: Date.now(),
        version: index.version,
      };

      localStorage.setItem(
        TaxonomySyncService.CACHE_KEY,
        JSON.stringify(cacheData)
      );
      logger.info(`💾 Cached taxonomy index (version ${index.version})`);
    } catch (error) {
      logger.warn('Failed to cache taxonomy index:', error);
    }
  }

  // Clear cache
  clearCache(): void {
    try {
      localStorage.removeItem(TaxonomySyncService.CACHE_KEY);
      logger.info('🗑️ Taxonomy cache cleared');
    } catch (error) {
      logger.warn('Failed to clear cache:', error);
    }
  }

  // Start background sync process
  private startBackgroundSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      try {
        // Periodic sync - just sync the index
        await this.syncIndex();
      } catch (error) {
        logger.error('Background sync failed:', error);
        this.syncState.lastError =
          error instanceof Error ? error.message : 'Background sync failed';
        this.syncState.connectionState = 'error';
        this.notifyStateChange();
      }
    }, TaxonomySyncService.SYNC_INTERVAL);

    logger.info(
      `⏰ Background sync started (interval: ${
        TaxonomySyncService.SYNC_INTERVAL / 1000
      }s)`
    );
  }

  // Stop all background processes
  stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    this.syncState.isInitialized = false;
    this.syncState.connectionState = 'disconnected';

    logger.info('🛑 Taxonomy sync stopped');
    this.notifyStateChange();
  }

  // Get current sync state
  getSyncState(): SyncState {
    return { ...this.syncState };
  }

  // Subscribe to state changes
  onStateChange(listener: (state: SyncState) => void): () => void {
    this.eventListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.eventListeners.indexOf(listener);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of state changes
  private notifyStateChange(): void {
    const state = this.getSyncState();
    this.eventListeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        logger.error('Error in sync state listener:', error);
      }
    });
  }

  // Force manual sync
  async forceSync(): Promise<TaxonomyIndex> {
    logger.info('🔄 Force sync requested...');

    try {
      // Call our new refreshTaxonomy function for complete cache clearing
      await refreshTaxonomy();
    } catch (error) {
      logger.warn(
        'Enhanced taxonomy refresh failed, falling back to local cache clear:',
        error
      );
      this.clearCache();
    }

    return await this.syncIndex();
  }
}

export const taxonomySyncService = new TaxonomySyncService();
export type { TaxonomyIndex, SyncState };
